import { useState } from "react";
import { buildMasterPool, WORD_STATE } from "../../utils/words";
import { SessionHistory } from "../shared/SessionHistory";
import { BadgesPanel } from "../shared/BadgesPanel";
import { ForcePinResetButton } from "../shared/ForcePinResetButton";
import { ProfileSettings } from "../shared/ProfileSettings";

/**
 * PlannerOverview – The "Overview" tab in the new Family Planner design.
 * Combines the old ParentSummary + ChildReport into one planner-styled view.
 */
export function PlannerOverview({ data, users, children, onDataChange, onSwitchTab }) {
  const [childId, setChildId] = useState(children[0]?.id);
  const [showProfile, setShowProfile] = useState(false);
  const pendingRequests = (data.requests || []).filter(r => r.status === "pending");
  const today = new Date().toDateString();

  const child = children.find(c => c.id === childId);

  if (showProfile && child) {
    return <ProfileSettings user={child} data={data} onDataChange={onDataChange} onClose={() => setShowProfile(false)} />;
  }

  return (
    <>
      {/* ── Pending Rewards Alert ── */}
      {pendingRequests.length > 0 && (
        <div
          className="sticky-orange rounded-xl p-4 mb-6 shadow-md relative pulse-alert"
          style={{ transform: "rotate(-0.5deg)" }}
        >
          <div className="pushpin absolute -top-2 left-6" />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-xl">🎁</span>
              <span className="font-handwritten text-xl">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-red-500 text-white text-sm rounded-full mr-1">
                  {pendingRequests.length}
                </span>
                {" "}reward request{pendingRequests.length > 1 ? "s" : ""} pending
              </span>
            </div>
            <button
              onClick={() => onSwitchTab("rewards")}
              className="px-4 py-1 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 flex items-center gap-1"
            >
              View
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Child Summary Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
        {children.map(c => (
          <ChildSummaryCard
            key={c.id}
            child={c}
            data={data}
            today={today}
            tapeColor={c.id === children[0]?.id ? "bg-pink-300" : "bg-blue-300"}
            avatarGrad={c.id === children[0]?.id ? "from-pink-200 to-pink-300" : "from-blue-200 to-blue-300"}
          />
        ))}
      </div>

      {/* ── Detailed Report Section ── */}
      <div className="bg-white/80 rounded-xl border border-stone-200 p-4 md:p-6">
        {/* Child selector pills */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {children.map(c => (
            <button
              key={c.id}
              onClick={() => setChildId(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all
                ${childId === c.id
                  ? "bg-pink-100 text-pink-700 border-2 border-pink-300"
                  : "bg-stone-100 text-stone-600 border-2 border-transparent hover:bg-stone-200"
                }`}
            >
              {c.avatar} {c.name}
            </button>
          ))}
        </div>

        {child && <ChildDetailReport child={child} data={data} onDataChange={onDataChange} setShowProfile={setShowProfile} />}
      </div>
    </>
  );
}

/* ────────────────────────────────────────────────────────
   Child Summary Card (index-card style)
   ──────────────────────────────────────────────────────── */
function ChildSummaryCard({ child, data, today, tapeColor, avatarGrad }) {
  const sessions = (data.sessions || []).filter(s => s.userId === child.id);
  const spellSessions = sessions.filter(s => s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym");
  const streak = data.streaks?.[child.id] || 0;
  const points = data.points?.[child.id] || 0;
  const avgScore = spellSessions.length
    ? Math.round(spellSessions.reduce((a, b) => a + b.score, 0) / spellSessions.length)
    : null;
  const mastered = Object.entries(data.progress || {})
    .filter(([k, v]) => k.startsWith(child.id) && !k.includes("_meaning_") && v.mastered).length;
  // Today status
  const doneToday = sessions.some(s => s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym" && new Date(s.date).toDateString() === today);

  return (
    <div className="index-card rounded-xl p-5 hover-lift relative">
      <div className={`absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-4 ${tapeColor} tape rounded washi-stripe`} />

      {/* Avatar + name + points */}
      <div className="flex items-start gap-4">
        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${avatarGrad} flex items-center justify-center text-2xl border-4 border-white shadow-md flex-shrink-0`}>
          {child.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-handwritten text-2xl text-stone-800">{child.name}</h3>
          <p className="text-stone-500 text-sm">Year {child.yearLevel}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
              {streak} day streak
            </span>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
              {points.toLocaleString()} pts
            </span>
          </div>
        </div>
      </div>

      {/* Quick stats grid */}
      <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs">
        <div className="bg-stone-50 rounded-lg p-2">
          <p className="font-bold text-stone-800">{sessions.length}</p>
          <p className="text-stone-500">Tests</p>
        </div>
        <div className="bg-stone-50 rounded-lg p-2">
          <p className={`font-bold ${avgScore && avgScore >= 70 ? "text-green-600" : "text-amber-600"}`}>
            {avgScore !== null ? `${avgScore}%` : "—"}
          </p>
          <p className="text-stone-500">Avg</p>
        </div>
        <div className="bg-stone-50 rounded-lg p-2">
          <p className="font-bold text-stone-800">{mastered}</p>
          <p className="text-stone-500">Words</p>
        </div>
        <div className="bg-stone-50 rounded-lg p-2">
          <p className={`font-bold ${doneToday ? "text-green-600" : "text-stone-400"}`}>
            {doneToday ? "✅" : "⏳"}
          </p>
          <p className="text-stone-500">Today</p>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Child Detail Report (inside the white panel)
   ──────────────────────────────────────────────────────── */
function ChildDetailReport({ child, data, onDataChange, setShowProfile }) {
  const sessions = (data.sessions || []).filter(s => s.userId === child.id);
  const poolSize = buildMasterPool(data.wordLists).length;

  // Spelling progress
  const myProgress = Object.entries(data.progress || {}).filter(([k]) => k.startsWith(child.id) && !k.includes("_meaning_"));
  const attempted = myProgress.filter(([, v]) => v.attempts > 0).length;
  const mastered = myProgress.filter(([, v]) => v.mastered).length;
  const confident = myProgress.filter(([, v]) => v.state === WORD_STATE.CONFIDENT).length;
  const learning = myProgress.filter(([, v]) => v.state === WORD_STATE.LEARNING).length;

  // Meaning progress
  const myMeaningProgress = Object.entries(data.meaningProgress || {}).filter(([k]) => k.startsWith(child.id));
  const meaningAttempted = myMeaningProgress.filter(([, v]) => v.attempts > 0).length;
  const meaningMastered = myMeaningProgress.filter(([, v]) => v.mastered).length;
  const meaningConfident = myMeaningProgress.filter(([, v]) => v.state === WORD_STATE.CONFIDENT).length;
  const meaningLearning = myMeaningProgress.filter(([, v]) => v.state === WORD_STATE.LEARNING).length;

  // Most missed
  const missedCounts = {};
  sessions.forEach(s => (s.missed || []).forEach(w => { missedCounts[w] = (missedCounts[w] || 0) + 1; }));
  const topMissed = Object.entries(missedCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

  return (
    <>
      {/* ── Progress sticky notes ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        {/* Spelling Progress */}
        <div className="sticky-yellow rounded-xl p-4 shadow-sm" style={{ transform: "rotate(-0.3deg)" }}>
          <h4 className="font-handwritten text-xl mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center text-white text-sm">S</span>
            Spelling Progress
          </h4>
          <ProgressGrid
            newCount={poolSize - attempted}
            learning={learning}
            confident={confident}
            mastered={mastered}
          />
        </div>

        {/* Meaning Progress */}
        <div className="sticky-blue rounded-xl p-4 shadow-sm" style={{ transform: "rotate(0.3deg)" }}>
          <h4 className="font-handwritten text-xl mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center text-white text-sm">M</span>
            Meaning Progress
          </h4>
          <ProgressGrid
            newCount={poolSize - meaningAttempted}
            learning={meaningLearning}
            confident={meaningConfident}
            mastered={meaningMastered}
          />
        </div>
      </div>

      {/* ── Recent Tests ── */}
      <div className="mb-6">
        <h4 className="font-handwritten text-xl mb-3">Recent Tests</h4>
        {sessions.length === 0 ? (
          <p className="text-stone-500 text-sm py-4">No tests yet.</p>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 overflow-x-auto">
            <SessionHistory sessions={sessions} />
          </div>
        )}
      </div>

      {/* ── Bottom row: missed words + badges ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {topMissed.length > 0 && (
          <div className="sticky-pink rounded-xl p-4 shadow-sm" style={{ transform: "rotate(0.2deg)" }}>
            <h4 className="font-handwritten text-xl mb-3">Words to Review</h4>
            <div className="flex flex-wrap gap-2">
              {topMissed.map(([w, n]) => (
                <span key={w} className="px-3 py-1 bg-white/80 rounded-lg text-sm border border-pink-200">
                  {w} <span className="text-pink-400 text-xs">x{n}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="sticky-green rounded-xl p-4 shadow-sm" style={{ transform: "rotate(-0.2deg)" }}>
          <h4 className="font-handwritten text-xl mb-3">Badges</h4>
          <BadgesPanel userId={child.id} data={data} />
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="mt-6 flex gap-3 flex-wrap">
        <button
          className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200 transition-all"
          onClick={() => setShowProfile(true)}
        >
          ✏️ Edit {child.name}'s Profile
        </button>
        <ForcePinResetButton child={child} data={data} onDataChange={onDataChange} />
      </div>
    </>
  );
}

/* ────────────────────────────────────────────────────────
   Small shared component: 4-column progress grid
   ──────────────────────────────────────────────────────── */
function ProgressGrid({ newCount, learning, confident, mastered }) {
  const items = [
    { label: "New",       val: newCount,   color: "text-stone-800" },
    { label: "Learning",  val: learning,   color: "text-amber-600" },
    { label: "Confident", val: confident,  color: "text-blue-600"  },
    { label: "Mastered",  val: mastered,   color: "text-green-600" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
      {items.map(({ label, val, color }) => (
        <div key={label} className="bg-white/70 rounded-lg p-2">
          <p className={`font-bold ${color}`}>{val}</p>
          <p className="text-xs text-stone-500">{label}</p>
        </div>
      ))}
    </div>
  );
}
